import { useEffect, useState } from "react";
import { Plus, FileText, Layers, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MediaUploader from "@/components/MediaUploader";
import MediaEmbed from "@/components/MediaEmbed";
import ExerciseBuilder from "@/components/exercises/ExerciseBuilder";

const EMPTY_LESSON = { title: "", explanation: "", content: "", status: "published", module_id: "", media: [] };

// Shared course content editor (modules → lessons → exercises). Used by both the
// Teacher and Admin dashboards so they manage native lessons the same way.
export default function CourseEditor({ courseId, onClose }) {
  const { t } = useI18n();
  const [course, setCourse] = useState(null);
  const [moduleForm, setModuleForm] = useState({ title: "", description: "" });
  const [moduleOpen, setModuleOpen] = useState(false);
  const [lessonForm, setLessonForm] = useState(EMPTY_LESSON);
  const [lessonOpen, setLessonOpen] = useState(false);
  const [exerciseLesson, setExerciseLesson] = useState(null);

  const loadCourse = async (id) => {
    try { const { data } = await api.get(`/courses/${id}`); setCourse(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  useEffect(() => {
    if (courseId) loadCourse(courseId);
    else setCourse(null);
  }, [courseId]);

  const refresh = () => courseId && loadCourse(courseId);

  const createModule = async (e) => {
    e.preventDefault();
    try {
      await api.post("/modules", { course_id: courseId, ...moduleForm });
      toast.success(t("teacher.new_module")); setModuleOpen(false);
      setModuleForm({ title: "", description: "" });
      refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openLessonDialog = (moduleId) => { setLessonForm({ ...EMPTY_LESSON, module_id: moduleId }); setLessonOpen(true); };

  const createLesson = async (e) => {
    e.preventDefault();
    try {
      await api.post("/lessons", {
        course_id: courseId,
        module_id: lessonForm.module_id || null,
        title: lessonForm.title,
        explanation: lessonForm.explanation,
        content: lessonForm.content,
        status: lessonForm.status,
        media: lessonForm.media,
      });
      toast.success(t("teacher.add_lesson")); setLessonOpen(false);
      setLessonForm(EMPTY_LESSON);
      refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const reorder = async (path, orderedIds) => {
    try { await api.post(path, { ordered_ids: orderedIds }); refresh(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const moveModule = (idx, dir) => {
    const arr = [...course.modules]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    reorder("/modules/reorder", arr.map((m) => m.id));
  };
  const moveLesson = (module, idx, dir) => {
    const arr = [...module.lessons]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    reorder("/lessons/reorder", arr.map((l) => l.id));
  };

  return (
    <>
      {/* Course detail — modules & lessons */}
      <Dialog open={!!courseId} onOpenChange={(o) => !o && onClose?.()}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[88vh] overflow-y-auto">
          {course && (
            <>
              <DialogHeader><DialogTitle className="text-2xl">{course.title}</DialogTitle></DialogHeader>
              <div className="flex justify-end">
                <Button size="sm" data-testid="open-create-module-button" onClick={() => setModuleOpen(true)} className="rounded-full"><Plus className="h-4 w-4 mr-1" />{t("teacher.add_module")}</Button>
              </div>
              <div className="space-y-4">
                {(course.modules || []).map((m, mi) => (
                  <div key={m.id} data-testid={`teacher-module-${m.id}`} className="rounded-2xl border border-border p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Layers className="h-5 w-5 text-secondary shrink-0" />
                      <h3 className="font-heading font-bold text-lg flex-1 min-w-0 truncate">{m.title}</h3>
                      <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" disabled={mi === 0} onClick={() => moveModule(mi, -1)} data-testid={`module-up-${m.id}`} title={t("teacher.move_up")}><ArrowUp className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" disabled={mi === course.modules.length - 1} onClick={() => moveModule(mi, 1)} data-testid={`module-down-${m.id}`} title={t("teacher.move_down")}><ArrowDown className="h-4 w-4" /></Button>
                      <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={() => openLessonDialog(m.id)} data-testid={`add-lesson-${m.id}`}><Plus className="h-3.5 w-3.5 mr-1" />{t("teacher.add_lesson")}</Button>
                    </div>
                    {m.description && <p className="text-sm text-muted-foreground">{m.description}</p>}
                    <div className="space-y-2">
                      {m.lessons.map((l, li) => (
                        <div key={l.id} data-testid={`lesson-item-${l.id}`} className="rounded-xl border border-border p-3 bg-muted/30 space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <FileText className="h-4 w-4 text-secondary shrink-0" />
                            <span className="font-semibold flex-1 min-w-0 truncate">{l.title}</span>
                            <Badge variant={l.status === "published" ? "secondary" : "outline"} className="rounded-full text-[10px]">{l.status === "published" ? t("teacher.published") : t("teacher.draft")}</Badge>
                            <Button size="icon" variant="ghost" className="h-7 w-7 rounded-full" disabled={li === 0} onClick={() => moveLesson(m, li, -1)} data-testid={`lesson-up-${l.id}`}><ArrowUp className="h-3.5 w-3.5" /></Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 rounded-full" disabled={li === m.lessons.length - 1} onClick={() => moveLesson(m, li, 1)} data-testid={`lesson-down-${l.id}`}><ArrowDown className="h-3.5 w-3.5" /></Button>
                            <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={() => setExerciseLesson(l)} data-testid={`add-exercise-button-${l.id}`}><Plus className="h-3.5 w-3.5 mr-1" />{t("teacher.add_exercise")}</Button>
                          </div>
                          {l.content && <p className="text-xs text-muted-foreground line-clamp-2">{l.content}</p>}
                          {(l.media || []).map((md, i) => <MediaEmbed key={i} media={md} />)}
                          {(l.exercises || []).length > 0 && (
                            <div className="flex flex-wrap gap-1.5">{l.exercises.map((ex) => <Badge key={ex.id} variant="secondary" className="rounded-full text-[10px]">{t(`ex.${ex.type}`)}</Badge>)}</div>
                          )}
                        </div>
                      ))}
                      {m.lessons.length === 0 && <p className="text-xs text-muted-foreground">{t("teacher.no_lessons")}</p>}
                    </div>
                  </div>
                ))}

                {(course.lessons || []).length > 0 && (
                  <div className="rounded-2xl border border-border p-4 space-y-3">
                    <h3 className="font-heading font-bold text-lg">{t("teacher.no_module")}</h3>
                    {course.lessons.map((l) => (
                      <div key={l.id} data-testid={`lesson-item-${l.id}`} className="rounded-xl border border-border p-3 bg-muted/30 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <FileText className="h-4 w-4 text-secondary shrink-0" />
                          <span className="font-semibold flex-1 min-w-0 truncate">{l.title}</span>
                          <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={() => setExerciseLesson(l)} data-testid={`add-exercise-button-${l.id}`}><Plus className="h-3.5 w-3.5 mr-1" />{t("teacher.add_exercise")}</Button>
                        </div>
                        {(l.exercises || []).length > 0 && (
                          <div className="flex flex-wrap gap-1.5">{l.exercises.map((ex) => <Badge key={ex.id} variant="secondary" className="rounded-full text-[10px]">{t(`ex.${ex.type}`)}</Badge>)}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {(course.modules || []).length === 0 && (course.lessons || []).length === 0 && <p className="text-sm text-muted-foreground">{t("teacher.no_modules")}</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create module */}
      <Dialog open={moduleOpen} onOpenChange={setModuleOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="text-2xl">{t("teacher.new_module")}</DialogTitle></DialogHeader>
          <form onSubmit={createModule} className="space-y-4">
            <div className="space-y-2"><Label>{t("common.title")}</Label>
              <Input data-testid="module-title-input" required value={moduleForm.title} onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })} className="rounded-xl h-11" /></div>
            <div className="space-y-2"><Label>{t("common.description")}</Label>
              <Textarea value={moduleForm.description} onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })} className="rounded-xl" /></div>
            <DialogFooter><Button type="submit" data-testid="submit-module-button" className="rounded-full h-11 px-6">{t("common.create")}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create lesson */}
      <Dialog open={lessonOpen} onOpenChange={setLessonOpen}>
        <DialogContent className="rounded-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-2xl">{t("teacher.add_lesson")}</DialogTitle></DialogHeader>
          <form onSubmit={createLesson} className="space-y-4">
            <div className="space-y-2"><Label>{t("teacher.module")}</Label>
              <Select value={lessonForm.module_id} onValueChange={(v) => setLessonForm({ ...lessonForm, module_id: v })}>
                <SelectTrigger className="rounded-xl h-11" data-testid="lesson-module-select"><SelectValue placeholder={t("teacher.select_module")} /></SelectTrigger>
                <SelectContent>{(course?.modules || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><Label>{t("common.title")}</Label>
              <Input data-testid="lesson-title-input" required value={lessonForm.title} onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })} className="rounded-xl h-11" /></div>
            <div className="space-y-2"><Label>{t("teacher.lesson_explanation")}</Label>
              <Textarea data-testid="lesson-explanation-input" value={lessonForm.explanation} onChange={(e) => setLessonForm({ ...lessonForm, explanation: e.target.value })} className="rounded-xl" /></div>
            <div className="space-y-2"><Label>{t("teacher.lesson_content")}</Label>
              <Textarea data-testid="lesson-content-input" value={lessonForm.content} onChange={(e) => setLessonForm({ ...lessonForm, content: e.target.value })} className="rounded-xl" /></div>
            <div className="space-y-2"><Label>{t("teacher.status")}</Label>
              <Select value={lessonForm.status} onValueChange={(v) => setLessonForm({ ...lessonForm, status: v })}>
                <SelectTrigger className="rounded-xl h-11" data-testid="lesson-status-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="published">{t("teacher.published")}</SelectItem><SelectItem value="draft">{t("teacher.draft")}</SelectItem></SelectContent></Select></div>
            <div className="space-y-2"><Label>{t("teacher.media")} / {t("common.pdf")}</Label>
              <MediaUploader media={lessonForm.media} onChange={(m) => setLessonForm({ ...lessonForm, media: m })} /></div>
            <DialogFooter><Button type="submit" data-testid="submit-lesson-button" className="rounded-full h-11 px-6">{t("common.create")}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create exercise */}
      <Dialog open={!!exerciseLesson} onOpenChange={(o) => !o && setExerciseLesson(null)}>
        <DialogContent className="rounded-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-2xl">{t("teacher.add_exercise")}</DialogTitle></DialogHeader>
          {exerciseLesson && (
            <ExerciseBuilder
              lessonId={exerciseLesson.id}
              courseId={courseId}
              language={course?.language || "en"}
              onCreated={() => { setExerciseLesson(null); refresh(); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
